"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import dynamic from "next/dynamic";
import gsap from "gsap";
import { setMotionLayer } from "./_lib/motionLayer";

const DotLottieReact = dynamic(
  () => import("@lottiefiles/dotlottie-react").then((m) => m.DotLottieReact),
  { ssr: false, loading: () => null }
);

interface SiteLoaderProps {
  isReady: boolean;
  onComplete: () => void;
}

const MIN_DISPLAY_MS = 2500;
const HOLD_AFTER_READY_MS = 1000;
const MAX_WAIT_MS = 10000;
const LOADER_LOTTIE = "/assets/pearson-loader.lottie";

export default function SiteLoader({ isReady, onComplete }: SiteLoaderProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const lottieWrapRef = useRef<HTMLDivElement>(null);

  const [removed, setRemoved] = useState(false);
  const mountTimeRef = useRef<number | null>(null);
  const exitingRef = useRef(false);

  const runExit = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;

    const overlay = overlayRef.current;
    const glow = glowRef.current;
    const lottieWrap = lottieWrapRef.current;
    if (!overlay) return;

    const exitTl = gsap.timeline({
      onComplete: () => {
        setMotionLayer(glow, false);
        setMotionLayer(lottieWrap, false);
        document.body.style.overflow = "";
        setRemoved(true);
        onComplete();
      },
    });

    if (lottieWrap) {
      exitTl.to(
        lottieWrap,
        { scale: 1.05, opacity: 0, duration: 0.45, ease: "power2.in" },
        0
      );
    }

    if (glow) {
      exitTl.to(
        glow,
        { scale: 2.2, opacity: 0, duration: 0.55, ease: "power2.in" },
        0.05
      );
    }

    exitTl.to(
      overlay,
      { opacity: 0, duration: 0.35, ease: "power1.in" },
      0.2
    );
  }, [onComplete]);

  useEffect(() => {
    mountTimeRef.current = Date.now();
    const maxWaitTimer = setTimeout(runExit, MAX_WAIT_MS);
    return () => clearTimeout(maxWaitTimer);
  }, [runExit]);

  useEffect(() => {
    if (!isReady || mountTimeRef.current === null) return;

    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = Math.max(HOLD_AFTER_READY_MS, MIN_DISPLAY_MS - elapsed);

    const timer = setTimeout(runExit, remaining);
    return () => clearTimeout(timer);
  }, [isReady, runExit]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const overlay = overlayRef.current;
    const glow = glowRef.current;
    const lottieWrap = lottieWrapRef.current;
    if (!overlay || !glow || !lottieWrap) return;

    gsap.set(glow, { opacity: 0, scale: 0.85 });
    gsap.set(lottieWrap, { opacity: 0, scale: 0.96 });

    setMotionLayer(glow, true);
    setMotionLayer(lottieWrap, true);

    const entranceTl = gsap.timeline();

    entranceTl.to(glow, {
      opacity: 1,
      scale: 1,
      duration: 0.6,
      ease: "power2.out",
    });

    entranceTl.to(
      lottieWrap,
      {
        opacity: 1,
        scale: 1,
        duration: 0.55,
        ease: "power2.out",
      },
      0.15
    );

    return () => {
      entranceTl.kill();
    };
  }, []);

  if (removed) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0D004D]"
      role="status"
      aria-label="Loading"
    >
      <div
        ref={glowRef}
        className="absolute pointer-events-none"
        style={{
          width: "min(500px, 80vw)",
          height: "min(500px, 80vw)",
        }}
      >
        <div
          className="h-full w-full loader-glow-pulse"
          style={{
            background:
              "radial-gradient(circle, rgba(167,139,250,0.35) 0%, rgba(167,139,250,0.08) 40%, transparent 70%)",
          }}
        />
      </div>

      <div
        ref={lottieWrapRef}
        className="relative w-[min(320px,78vw)] aspect-square"
      >
        <DotLottieReact
          src={LOADER_LOTTIE}
          loop
          autoplay
          className="h-full w-full"
        />
      </div>
    </div>
  );
}
